/*
==========================================================
DH Commerce Coach
Version: 1.0.0
Author: OpenAI + DH Foods

Purpose:

• Interprets promotion and cart state
• Determines the customer's next best step
• Provides premium, brand-appropriate coaching language
• Emits coach lifecycle events
• Does not directly manipulate homepage HTML

Dependencies:

• dh-commerce-framework.js
• DHCommerce.promotions
• DHCommerce.state
• DHCommerce event bus

==========================================================
*/

(function (window, document) {

  "use strict";

  const REQUIRED_FRAMEWORK_VERSION = "1.2.0";
  const COACH_VERSION = "1.0.0";

  let initialized = false;


  /* ======================================================
     FRAMEWORK ACCESS
  ====================================================== */

  function getCommerce() {
    return window.DHCommerce || null;
  }

  function frameworkIsReady() {
    const commerce = getCommerce();

    return Boolean(
      commerce &&
      commerce.version &&
      commerce.promotions &&
      typeof commerce.promotions.getCurrent === "function" &&
      typeof commerce.on === "function" &&
      typeof commerce.emit === "function"
    );
  }


  /* ======================================================
     HELPERS
  ====================================================== */

  function normalizeAmount(value) {
    const amount = Number(value);

    return Number.isFinite(amount)
      ? Math.max(amount, 0)
      : 0;
  }

  function formatCurrency(value) {
    const amount = normalizeAmount(value);

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits:
        Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

function getCurrentRecommendation() {
  const commerce = getCommerce();

  if (
    !commerce ||
    !commerce.recommendations ||
    typeof commerce.recommendations.getCurrent !==
      "function"
  ) {
    return null;
  }

  const result =
    commerce.recommendations.getCurrent();

  if (
    !result ||
    !result.active ||
    !result.recommendation
  ) {
    return null;
  }

  return result;
}

  /* ======================================================
     ADVICE RESOLVER
  ====================================================== */

  function resolveAdvice(evaluation) {
    if (
      !evaluation ||
      !evaluation.promotion
    ) {
      return {
        type: "inactive",
        priority: 0,
        headline: "",
        advice: "",
        target: null,
        remaining: 0,
        promotionId: null,
        shippingTier: null
      };
    }

    const promotion = evaluation.promotion;

    const shippingUnlocked =
      Boolean(evaluation.freeShippingUnlocked);

    const giftUnlocked =
      Boolean(evaluation.giftUnlocked);

    const amountUntilShipping =
      normalizeAmount(
        evaluation.amountUntilFreeShipping
      );

    const amountUntilGift =
      normalizeAmount(
        evaluation.amountUntilGift
      );

    if (!shippingUnlocked) {
  const recommendation =
    getCurrentRecommendation();

  let advice =
    "Add " +
    formatCurrency(amountUntilShipping) +
    " more to unlock free shipping.";

  if (
    recommendation &&
    recommendation.target &&
    recommendation.target.type ===
      "free_shipping" &&
    recommendation.recommendation
  ) {
    const product =
      recommendation.recommendation;

    advice =
      product.productTitle +
      " — " +
      product.packLabel +
      (
        product.reachesGoal
          ? " will unlock free shipping."
          : " will move you closer to free shipping."
      );
  }

  return {
    type: "promotion",
    priority: 100,
    headline: "You're getting close.",
    advice: advice,
    target: "free_shipping",
    remaining: amountUntilShipping,
    recommendation:
      recommendation
        ? recommendation.recommendation
        : null,
    promotionId: promotion.id,
    shippingTier: promotion.shippingTier
  };
}

    if (!giftUnlocked) {
  const recommendation =
    getCurrentRecommendation();

  const giftName =
    promotion.giftName ||
    "today's complimentary gift";

  let advice =
    "Add " +
    formatCurrency(amountUntilGift) +
    " more to receive " +
    giftName +
    ".";

  if (
    recommendation &&
    recommendation.target &&
    recommendation.target.type === "gift" &&
    recommendation.recommendation
  ) {
    const product =
      recommendation.recommendation;

    advice =
      product.productTitle +
      " — " +
      product.packLabel +
      (
        product.reachesGoal
          ? " will unlock " +
            giftName +
            "."
          : " will move you closer to " +
            giftName +
            "."
      );
  }

  return {
    type: "promotion",
    priority: 90,
    headline: "Free shipping is unlocked.",
    advice: advice,
    target: "gift",
    remaining: amountUntilGift,
    recommendation:
      recommendation
        ? recommendation.recommendation
        : null,
    promotionId: promotion.id,
    shippingTier: promotion.shippingTier
  };
}

    return {
      type: "complete",
      priority: 10,
      headline: "Excellent choice.",
      advice:
        "Your order qualifies for free shipping and " +
        (
          promotion.giftName ||
          "today's complimentary gift"
        ) +
        ".",
      target: "complete",
      remaining: 0,
      promotionId: promotion.id,
      shippingTier: promotion.shippingTier
    };
  }


  /* ======================================================
     PUBLIC SERVICE
  ====================================================== */

  function installCoachService() {
    const commerce = getCommerce();

    if (!commerce || initialized) {
      return;
    }

    initialized = true;

    commerce.coach = commerce.coach || {};

    commerce.coach.version = COACH_VERSION;

    commerce.coach.getCurrentAdvice = function () {
      const currentEvaluation =
        commerce.state &&
        commerce.state.coach &&
        commerce.state.coach.evaluation
          ? commerce.state.coach.evaluation
          : null;

      return resolveAdvice(currentEvaluation);
    };

    commerce.coach.resolve = function (evaluation) {
      return resolveAdvice(evaluation);
    };

    commerce.coach.getState = function () {
      return commerce.state.coach || null;
    };

    commerce.state.coach =
      commerce.state.coach &&
      typeof commerce.state.coach === "object"
        ? commerce.state.coach
        : {
            initialized: false,
            evaluation: null,
            advice: null
          };


    /* ======================================================
       EVENT CONNECTIONS
    ====================================================== */

    commerce.on(
      "promotion:evaluated",
      function (event) {
        const evaluation =
          event && event.payload
            ? event.payload
            : null;

        const advice =
          resolveAdvice(evaluation);

        commerce.state.coach.evaluation =
          evaluation;

        commerce.state.coach.advice =
          advice;

        commerce.emit(
          "coach:updated",
          {
            advice: advice,
            evaluation: evaluation
          }
        );
      }
    );

    commerce.on(
      "promotion:ready",
      function () {
        commerce.emit(
          "coach:ready",
          {
            version: COACH_VERSION,
            frameworkVersion:
              commerce.version ||
              REQUIRED_FRAMEWORK_VERSION
          }
        );
      }
    );

    commerce.state.coach.initialized = true;

    commerce.emit(
      "coach:initialized",
      {
        version: COACH_VERSION,
        frameworkVersion:
          commerce.version ||
          REQUIRED_FRAMEWORK_VERSION
      }
    );

    console.info(
      "[DH Commerce] Coach initialized.",
      {
        coachVersion: COACH_VERSION,
        frameworkVersion: commerce.version
      }
    );
  }


  /* ======================================================
     BOOTSTRAP
  ====================================================== */

  document.addEventListener(
    "dhcommerce",
    function (event) {
      const commerceEvent =
        event && event.detail
          ? event.detail
          : null;

      if (
        commerceEvent &&
        commerceEvent.name ===
          "framework:initialized"
      ) {
        installCoachService();
      }
    }
  );

  if (frameworkIsReady()) {
    installCoachService();
  }

})(window, document);